import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Heart, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";

const fmt = (cents) => new Intl.NumberFormat("en-US", {
  style: "currency", currency: "USD", maximumFractionDigits: 0,
}).format((cents || 0) / 100);

/**
 * Compact donation progress bar for Jen's LVA surgery fund.
 * Small variant lives in the footer; full variant is used on /about.
 */
export default function DonationProgress({ variant = "compact", onDonate }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/donations/progress").then((r) => setData(r.data)).catch(() => setData(null));
  }, []);

  if (!data) return null;
  const pct = Math.max(2, Math.min(100, data.percent || 0));
  const isCompact = variant === "compact";

  return (
    <div
      className={
        isCompact
          ? "max-w-md mx-auto rounded-3xl bg-white/70 backdrop-blur border border-kawaii-pink/30 p-4 mb-4"
          : "rounded-3xl bg-white border border-kawaii-pink/40 shadow-sm p-6 md:p-8"
      }
      data-testid="donation-progress"
    >
      <div className="flex items-center gap-2 mb-2">
        <Heart className="w-4 h-4 text-kawaii-pink" strokeWidth={2.5} fill="#F4A8CD" />
        <p className="text-[10px] uppercase tracking-[0.24em] text-kawaii-textSoft font-bold">
          Jen's LVA surgery fund
        </p>
      </div>

      <div className={`flex items-end justify-between mb-2 ${isCompact ? '' : 'flex-wrap gap-2'}`}>
        <div>
          <div className={`font-heading text-kawaii-text ${isCompact ? 'text-lg' : 'text-3xl md:text-4xl'}`}>
            {fmt(data.raised_usd_cents)}
            <span className="text-kawaii-textSoft font-body font-normal text-sm ml-1">/ {fmt(data.goal_usd_cents)}</span>
          </div>
          {!isCompact && data.donor_count > 0 && (
            <p className="text-xs text-kawaii-textSoft font-body mt-1">
              From {data.donor_count} kind {data.donor_count === 1 ? "person" : "people"} so far ♡
            </p>
          )}
        </div>
        <div className={`font-heading font-semibold text-kawaii-pink ${isCompact ? 'text-sm' : 'text-2xl'}`}>
          {data.percent.toFixed(1)}%
        </div>
      </div>

      {/* Bar */}
      <div className={`w-full rounded-full bg-kawaii-border/60 overflow-hidden ${isCompact ? 'h-2' : 'h-4'}`}>
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${pct}%` }}
          transition={{ duration: 1.2, ease: "easeOut" }}
          className="h-full bg-gradient-to-r from-kawaii-pink via-kawaii-peach to-kawaii-lavender rounded-full relative overflow-hidden"
        >
          <motion.span
            className="absolute inset-0 -skew-x-12"
            animate={{ x: ["-100%", "200%"] }}
            transition={{ duration: 3.4, repeat: Infinity, ease: "easeInOut" }}
            style={{ background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.5), transparent)" }}
          />
        </motion.div>
      </div>

      {isCompact ? (
        <div className="mt-3 flex items-center justify-between gap-2">
          <Link to="/about" data-testid="about-jen-link" className="text-[11px] text-kawaii-textSoft underline underline-offset-2 hover:text-kawaii-text">
            About Jen ♡
          </Link>
          <button
            onClick={onDonate}
            data-testid="donate-from-progress"
            className="kawaii-btn text-xs px-3 py-1.5 rounded-full bg-gradient-to-r from-kawaii-pink to-kawaii-peach text-white font-body font-semibold inline-flex items-center gap-1.5"
          >
            <Sparkles className="w-3 h-3" strokeWidth={2.5} /> Add to Jen's fund
          </button>
        </div>
      ) : (
        <button
          onClick={onDonate}
          data-testid="donate-from-progress-full"
          className="kawaii-btn mt-5 w-full py-3 rounded-full bg-gradient-to-r from-kawaii-pink to-kawaii-peach text-white font-heading font-semibold text-sm shadow-md inline-flex items-center justify-center gap-2"
        >
          <Heart className="w-4 h-4" strokeWidth={2.5} fill="currentColor" />
          Support Jen's surgery
        </button>
      )}
    </div>
  );
}
